/**
 * ADP EPIP layout — the record-typed payroll input file (HDR / DET / TRL).
 *
 * WHY A SECOND ADP FORMAT: the WFN paydata grid (`adpPaydataPreset.ts`) is one
 * row per associate with fixed Reg/OT columns and Code/Amount slots. EPIP is the
 * older ADP payroll input layout that many ADP Enterprise clients still load:
 * one HEADER record that opens the batch, one DETAIL record per associate per
 * earnings code, and one TRAILER record carrying control totals ADP checks
 * before it accepts the batch. Codan's ADP payroll reads this layout.
 *
 * The record layout is the one Dan Baek described (2026-09-18), with the
 * decisions Josh took on 2026-09-21 for Codan (this is the spec we hand ADP):
 *
 *   HDR  Company Code, Pay Period Start, Pay Period End, Total Record Count,
 *        Batch Type (REGULAR | RETRO)
 *   DET  Employee Number, Last Name, First Name, Earnings Code, Hours,
 *        Amount, Cost Center
 *   TRL  Total Employees, Total Hours, Total Amount
 *
 *   - dates MM/DD/YYYY (we define it; ADP does not)
 *   - hours only: Amount is always blank, Total Amount always 0.00
 *   - Company Code = the SAP S/4 company code (e.g. 8010)
 *   - Earnings Code = the S/4-side code: the SAP absence type (AWART) on leave
 *     lines, the Time Evaluation earnings code on worked / premium lines
 *   - Employee Number = the SAP personnel number, as SAP holds it (8 digits)
 *   - a heading line sits above each block; its first field is 'Record Type',
 *     which is the rule a loader uses to skip it
 *   - retro corrections go in a SEPARATE file with Batch Type RETRO
 *
 * ⚠️ ADP configures EPIP per client (delimiter vs fixed width, date format,
 * whether Amount is populated, what the record count counts). Nothing here is
 * hardcoded into the serializer: the field order, the delimiter, the widths and
 * every one of those choices is DATA on the profile, so matching the client's
 * spec is an edit in the profile editor, not a code change. The shipped
 * `DEFAULT_EPIP_LAYOUT` is the spec above.
 *
 * ⚠️ EPIP itself has no retro recalculation (one reason ADP is moving clients off
 * it). Corrections to a period already sent are therefore a SEPARATE batch:
 * Batch Type RETRO, carrying the original pay period and only the hour deltas
 * against what was sent (`buildAdpEpipRetroFile`, fed by the export ledger).
 *
 * PURE. No I/O, no DOM.
 */

// ─── Field model ───────────────────────────────────────────────────────────────

/**
 * What a field in a record carries.
 *
 * Header tokens: `coCode`, `batchId`, `periodStart`, `periodEnd`, `recordCount`.
 * Detail tokens: `fileNumber`, `lastName`, `firstName`, `employeeName`,
 *   `earningsCode`, `hours`, `amount`, `department`, `workDate`, `sapEmployeeNo`,
 *   `payrollTenant`.
 * Trailer tokens: `employeeCount`, `detailCount`, `totalHours`, `totalAmount`.
 * Any record: `recordType`, `coCode`, `batchId`, `periodStart`, `periodEnd`,
 *   `literal`, `blank`.
 *
 * A token used on a record that cannot supply it (a `lastName` on the trailer)
 * renders empty and is reported by `validateEpipLayout`.
 */
export type EpipFieldToken =
  | 'recordType'
  | 'coCode'
  | 'batchId'
  | 'periodStart'
  | 'periodEnd'
  | 'recordCount'
  | 'batchType'
  | 'fileNumber'
  | 'lastName'
  | 'firstName'
  | 'employeeName'
  | 'earningsCode'
  | 'hours'
  | 'amount'
  | 'department'
  | 'workDate'
  | 'sapEmployeeNo'
  | 'payrollTenant'
  | 'employeeCount'
  | 'detailCount'
  | 'totalHours'
  | 'totalAmount'
  | 'literal'
  | 'blank'

export interface EpipField {
  token: EpipFieldToken
  /** Only for `token: 'literal'`. */
  value?: string
  /** Fixed-width layout only: the field's width in characters. */
  width?: number
  /** Fixed-width layout only. Defaults: numbers right, everything else left. */
  align?: 'left' | 'right'
  /** Fixed-width layout only. Default ' ' (numbers may want '0'). */
  padChar?: string
  /**
   * Column heading written on the heading line above the block. Defaults to
   * `EPIP_TOKEN_HEADING`. Ignored on the record-type field, whose heading is
   * always 'Record Type' because that is how a loader recognises and skips a
   * heading line.
   */
  heading?: string
}

export type EpipRecordKind = 'header' | 'detail' | 'trailer'

/** Which tokens each record can actually supply. */
export const EPIP_TOKENS_BY_RECORD: Record<EpipRecordKind, readonly EpipFieldToken[]> = {
  header: ['recordType', 'coCode', 'batchId', 'batchType', 'periodStart', 'periodEnd', 'recordCount', 'detailCount', 'employeeCount', 'totalHours', 'totalAmount', 'literal', 'blank'],
  detail: ['recordType', 'coCode', 'batchId', 'periodStart', 'periodEnd', 'fileNumber', 'lastName', 'firstName', 'employeeName', 'earningsCode', 'hours', 'amount', 'department', 'workDate', 'sapEmployeeNo', 'payrollTenant', 'literal', 'blank'],
  trailer: ['recordType', 'coCode', 'batchId', 'batchType', 'periodStart', 'periodEnd', 'recordCount', 'employeeCount', 'detailCount', 'totalHours', 'totalAmount', 'literal', 'blank'],
}

/** Tokens rendered as 2dp numbers. */
export const EPIP_NUMERIC_TOKENS: ReadonlySet<EpipFieldToken> = new Set<EpipFieldToken>([
  'hours',
  'amount',
  'totalHours',
  'totalAmount',
])

/** Tokens rendered as integers. */
export const EPIP_COUNT_TOKENS: ReadonlySet<EpipFieldToken> = new Set<EpipFieldToken>([
  'recordCount',
  'employeeCount',
  'detailCount',
])

/** Tokens rendered as dates. */
export const EPIP_DATE_TOKENS: ReadonlySet<EpipFieldToken> = new Set<EpipFieldToken>([
  'periodStart',
  'periodEnd',
  'workDate',
])

export const EPIP_TOKEN_LABEL: Record<EpipFieldToken, string> = {
  recordType: 'Record type',
  coCode: 'Company code',
  batchId: 'Batch ID',
  periodStart: 'Pay period start',
  periodEnd: 'Pay period end',
  recordCount: 'Total record count',
  batchType: 'Batch type (REGULAR / RETRO)',
  fileNumber: 'Employee File / ID #',
  lastName: 'Last name',
  firstName: 'First name',
  employeeName: 'Full name',
  earningsCode: 'Earning code',
  hours: 'Units / hours',
  amount: 'Amount',
  department: 'Cost center / department',
  workDate: 'Work date',
  sapEmployeeNo: 'SAP personnel #',
  payrollTenant: 'Payroll tenant',
  employeeCount: 'Total employees',
  detailCount: 'Total detail lines',
  totalHours: 'Total hours',
  totalAmount: 'Total gross amount',
  literal: 'Literal',
  blank: 'Blank',
}

/** The heading text written above each block, per field. */
export const EPIP_TOKEN_HEADING: Record<EpipFieldToken, string> = {
  recordType: 'Record Type',
  coCode: 'Company Code',
  batchId: 'Batch ID',
  periodStart: 'Pay Period Start',
  periodEnd: 'Pay Period End',
  recordCount: 'Record Count',
  batchType: 'Batch Type',
  fileNumber: 'Employee Number',
  lastName: 'Last Name',
  firstName: 'First Name',
  employeeName: 'Employee Name',
  earningsCode: 'Earnings Code',
  hours: 'Hours',
  amount: 'Amount',
  department: 'Cost Center',
  workDate: 'Work Date',
  sapEmployeeNo: 'SAP Personnel Number',
  payrollTenant: 'Payroll Tenant',
  employeeCount: 'Total Employees',
  detailCount: 'Total Detail Records',
  totalHours: 'Total Hours',
  totalAmount: 'Total Amount',
  literal: '',
  blank: '',
}

/** The heading of one field: 'Record Type' for the tag, else the override or the default. */
export function epipFieldHeading(f: EpipField): string {
  if (f.token === 'recordType') return EPIP_TOKEN_HEADING.recordType
  return (f.heading ?? '').trim() || EPIP_TOKEN_HEADING[f.token] || ''
}

// ─── Layout ────────────────────────────────────────────────────────────────────

export type EpipDelimiter = ',' | '|' | '\t' | 'fixed'
export type EpipDateFormat = 'MM/DD/YYYY' | 'MMDDYYYY' | 'YYYYMMDD' | 'YYYY-MM-DD'

export interface AdpEpipLayout {
  /** Delimited, or `'fixed'` for a fixed-width file (every field needs a width). */
  delimiter: EpipDelimiter
  /** The literal record-type tag on each record. */
  recordTypes: { header: string; detail: string; trailer: string }
  headerFields: EpipField[]
  detailFields: EpipField[]
  trailerFields: EpipField[]
  dateFormat: EpipDateFormat
  /**
   * What the header's `recordCount` counts. `'detail'` = DET records only;
   * `'all'` = HDR + DET + TRL. ADP clients differ, and a wrong count rejects the
   * batch, which is exactly why it is a setting.
   */
  recordCountBasis: 'detail' | 'all'
  /**
   * What goes in a DETAIL record's Amount for HOURS lines.
   *
   * `'blank'` (default): hours only, ADP prices them from the associate's own
   * rate and the earning code's configured factor. ADP stays the rate system of
   * record, which is the safe default when the base rates live in ADP.
   *
   * `'evaluated'`: the Time Evaluation engine's dollars. Tesseract becomes the
   * rate source. A worker with hours but no base rate is BLOCKED rather than
   * sent at $0.00, because a zero Amount can pay nothing.
   *
   * Flat-dollar lines (lead, hazard, jury, bereavement) carry their dollars
   * in these two modes: they have no hours, so the Amount is the only way to
   * pay them.
   *
   * `'none'` (Codan, 2026-09-21): HOURS ONLY. Amount is never written, on any
   * line. A worker with a dollar-only earning (a flat premium) is BLOCKED,
   * because an hours-only file cannot pay it and dropping it would underpay
   * silently. Flat premiums have to be keyed in ADP.
   */
  amountMode: 'none' | 'blank' | 'evaluated'
  /** Where the ADP File # comes from. `'sap_pernr'` = the SAP personnel number. */
  fileNumberSource: 'crosswalk' | 'sap_pernr'
  /**
   * What the HDR company code carries. `'sap_company_code'` = the worker's SAP
   * company code (the payroll tenant on the S/4 build), no mapping needed.
   * `'adp_map'` = the profile's `companyCodeByTenant` (an ADP company code).
   */
  companyCodeSource: 'sap_company_code' | 'adp_map'
  /**
   * What the DET earnings code carries. `'sap'` = the S/4-side code, untranslated:
   * the SAP absence type (AWART) on leave lines, the Time Evaluation earnings
   * code on worked and premium lines. `'adp_map'` = the profile's
   * `earningsCodeMap`, then the catalog `hcm_code`.
   */
  earningsCodeSource: 'sap' | 'adp_map'
  /** Write a heading line above each block (HDR, DET, TRL). */
  includeHeadings: boolean
  /** Where DETAIL `department` comes from. */
  departmentSource: 'cost_center' | 'none'
  /** SAP cost center -> ADP department code. Unmapped falls back to the cost center itself. */
  departmentMap: Record<string, string>
  /**
   * Strip SAP's leading zeros from the cost center ('0000801510' -> '801510',
   * how SAP displays it). Never applied to the personnel number: when it is the
   * ADP employee number it is written exactly as SAP holds it.
   */
  stripLeadingZeros: boolean
  /** Upper-case names (ADP's own sample file carries DOE / JOHN). */
  uppercaseNames: boolean
  /** Delimited only: quote a value containing the delimiter, a quote or a newline. */
  quoteMode: 'minimal' | 'none'
  /**
   * Filename template, same tokens as the batch id ({CO_CODE}, {PERIOD_END:YYYYMMDD},
   * {PERIOD_START}, {TENANT}, {SEQ}). The extension is added: .csv for a comma
   * file, .txt otherwise.
   */
  fileNameTemplate: string
  /** Filename template for a RETRO batch. Same tokens; {SEQ} is the retro number for the period. */
  retroFileNameTemplate: string
  /** End the last record (the TRL) with a line terminator. Most flat-file loaders expect it. */
  trailingNewline: boolean
}

/**
 * The EPIP spec as defined for Codan's ADP (Dan Baek's layout, 2026-09-18, plus
 * Josh's decisions of 2026-09-21): comma delimited, MM/DD/YYYY, hours only,
 * SAP company code, S/4 earnings code, SAP personnel number as the employee
 * number, a heading line above each block, retros in a separate file.
 */
export const DEFAULT_EPIP_LAYOUT: AdpEpipLayout = {
  delimiter: ',',
  recordTypes: { header: 'HDR', detail: 'DET', trailer: 'TRL' },
  headerFields: [
    { token: 'recordType' },
    { token: 'coCode' },
    { token: 'periodStart' },
    { token: 'periodEnd' },
    { token: 'recordCount' },
    { token: 'batchType' },
  ],
  detailFields: [
    { token: 'recordType' },
    { token: 'fileNumber' },
    { token: 'lastName' },
    { token: 'firstName' },
    { token: 'earningsCode' },
    { token: 'hours' },
    { token: 'amount' },
    { token: 'department' },
  ],
  trailerFields: [
    { token: 'recordType' },
    { token: 'employeeCount' },
    { token: 'totalHours' },
    { token: 'totalAmount' },
  ],
  dateFormat: 'MM/DD/YYYY',
  recordCountBasis: 'detail',
  amountMode: 'none',
  fileNumberSource: 'sap_pernr',
  companyCodeSource: 'sap_company_code',
  earningsCodeSource: 'sap',
  includeHeadings: true,
  departmentSource: 'cost_center',
  departmentMap: {},
  stripLeadingZeros: true,
  uppercaseNames: true,
  quoteMode: 'minimal',
  fileNameTemplate: 'EPIP_{CO_CODE}_{PERIOD_END:YYYYMMDD}',
  retroFileNameTemplate: 'EPIP_RETRO_{CO_CODE}_{PERIOD_END:YYYYMMDD}_{SEQ}',
  trailingNewline: true,
}

/** Deep copy, so an editor never mutates the shared default. */
export function cloneEpipLayout(base: AdpEpipLayout = DEFAULT_EPIP_LAYOUT): AdpEpipLayout {
  return {
    ...base,
    recordTypes: { ...base.recordTypes },
    headerFields: base.headerFields.map((f) => ({ ...f })),
    detailFields: base.detailFields.map((f) => ({ ...f })),
    trailerFields: base.trailerFields.map((f) => ({ ...f })),
    departmentMap: { ...base.departmentMap },
  }
}

/**
 * A stored layout with any missing field defaulted. A hand-edited jsonb row, or
 * a profile saved before a field existed, must not crash the serializer.
 */
export function normalizeEpipLayout(raw: Partial<AdpEpipLayout> | null | undefined): AdpEpipLayout {
  const d = DEFAULT_EPIP_LAYOUT
  const r = raw ?? {}
  return {
    delimiter: r.delimiter ?? d.delimiter,
    recordTypes: { ...d.recordTypes, ...(r.recordTypes ?? {}) },
    headerFields: Array.isArray(r.headerFields) ? r.headerFields.map((f) => ({ ...f })) : d.headerFields.map((f) => ({ ...f })),
    detailFields: Array.isArray(r.detailFields) ? r.detailFields.map((f) => ({ ...f })) : d.detailFields.map((f) => ({ ...f })),
    trailerFields: Array.isArray(r.trailerFields) ? r.trailerFields.map((f) => ({ ...f })) : d.trailerFields.map((f) => ({ ...f })),
    dateFormat: r.dateFormat ?? d.dateFormat,
    recordCountBasis: r.recordCountBasis ?? d.recordCountBasis,
    amountMode: r.amountMode ?? d.amountMode,
    fileNumberSource: r.fileNumberSource ?? d.fileNumberSource,
    companyCodeSource: r.companyCodeSource ?? d.companyCodeSource,
    earningsCodeSource: r.earningsCodeSource ?? d.earningsCodeSource,
    includeHeadings: r.includeHeadings ?? d.includeHeadings,
    departmentSource: r.departmentSource ?? d.departmentSource,
    departmentMap: { ...(r.departmentMap ?? {}) },
    stripLeadingZeros: r.stripLeadingZeros ?? d.stripLeadingZeros,
    uppercaseNames: r.uppercaseNames ?? d.uppercaseNames,
    quoteMode: r.quoteMode ?? d.quoteMode,
    fileNameTemplate: r.fileNameTemplate ?? d.fileNameTemplate,
    retroFileNameTemplate: r.retroFileNameTemplate ?? d.retroFileNameTemplate,
    trailingNewline: r.trailingNewline ?? d.trailingNewline,
  }
}

// ─── Formatting helpers ────────────────────────────────────────────────────────

/** ISO YYYY-MM-DD -> the layout's date format. Pure string math, no timezone. */
export function formatEpipDate(iso: string, format: EpipDateFormat): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec((iso ?? '').trim())
  if (!m) return (iso ?? '').trim()
  const [, y, mo, d] = m
  switch (format) {
    case 'MMDDYYYY':
      return `${mo}${d}${y}`
    case 'YYYYMMDD':
      return `${y}${mo}${d}`
    case 'YYYY-MM-DD':
      return `${y}-${mo}-${d}`
    case 'MM/DD/YYYY':
    default:
      return `${mo}/${d}/${y}`
  }
}

/** '0000801510' -> '801510'; an all-zero value keeps one zero; non-numeric is untouched. */
export function stripSapLeadingZeros(value: string): string {
  const v = (value ?? '').trim()
  if (!/^\d+$/.test(v)) return v
  const stripped = v.replace(/^0+/, '')
  return stripped === '' ? '0' : stripped
}

/**
 * Split a display name into last / first when S/4 did not supply them apart.
 * 'Doe, John' -> Doe / John. 'John Q Doe' -> Doe / John Q. One word -> last only.
 * Reported as a guess by the serializer, because it is one.
 */
export function splitDisplayName(name: string): { lastName: string; firstName: string } {
  const n = (name ?? '').trim().replace(/\s+/g, ' ')
  if (!n) return { lastName: '', firstName: '' }
  const comma = n.indexOf(',')
  if (comma > 0) {
    return { lastName: n.slice(0, comma).trim(), firstName: n.slice(comma + 1).trim() }
  }
  const parts = n.split(' ')
  if (parts.length === 1) return { lastName: parts[0] ?? '', firstName: '' }
  return { lastName: parts[parts.length - 1] ?? '', firstName: parts.slice(0, -1).join(' ') }
}

// ─── Validation ────────────────────────────────────────────────────────────────

/**
 * Config-time problems with a layout, as sentences (empty = clean). Everything
 * here is something ADP's loader would reject or something that would silently
 * produce an unpayable file.
 */
export function validateEpipLayout(layout: AdpEpipLayout): string[] {
  const problems: string[] = []
  const records: Array<{ kind: EpipRecordKind; label: string; fields: EpipField[] }> = [
    { kind: 'header', label: 'Header', fields: layout.headerFields ?? [] },
    { kind: 'detail', label: 'Detail', fields: layout.detailFields ?? [] },
    { kind: 'trailer', label: 'Trailer', fields: layout.trailerFields ?? [] },
  ]

  for (const [k, v] of Object.entries(layout.recordTypes ?? {})) {
    if (!String(v ?? '').trim()) problems.push(`The ${k} record type tag is blank.`)
  }
  const tags = Object.values(layout.recordTypes ?? {}).map((t) => String(t).trim().toUpperCase())
  if (new Set(tags).size !== tags.length) problems.push('Two record types share the same tag, so ADP cannot tell them apart.')

  for (const r of records) {
    if (r.fields.length === 0) {
      problems.push(`The ${r.label.toLowerCase()} record has no fields.`)
      continue
    }
    if (r.fields[0]?.token !== 'recordType') {
      problems.push(`The ${r.label.toLowerCase()} record does not start with its record type tag.`)
    }
    const allowed = EPIP_TOKENS_BY_RECORD[r.kind]
    for (const f of r.fields) {
      if (!allowed.includes(f.token)) {
        problems.push(`${r.label} field "${EPIP_TOKEN_LABEL[f.token] ?? f.token}" has no value on a ${r.label.toLowerCase()} record and would always be empty.`)
      }
      if (f.token === 'literal' && !String(f.value ?? '').length) {
        problems.push(`${r.label} record has a literal field with no value.`)
      }
      if (layout.delimiter === 'fixed' && !(Number.isInteger(f.width) && (f.width ?? 0) > 0)) {
        problems.push(`${r.label} field "${EPIP_TOKEN_LABEL[f.token] ?? f.token}" has no width, and the layout is fixed width.`)
      }
    }
  }

  const detailTokens = new Set((layout.detailFields ?? []).map((f) => f.token))
  if (!detailTokens.has('fileNumber')) problems.push('The detail record has no Employee File / ID # field, so ADP cannot match a line to an associate.')
  if (!detailTokens.has('earningsCode')) problems.push('The detail record has no Earning code field.')
  if (!detailTokens.has('hours') && !detailTokens.has('amount')) problems.push('The detail record carries neither hours nor an amount, so nothing would be paid.')
  if (layout.amountMode === 'evaluated' && !detailTokens.has('amount')) {
    problems.push('Amount mode is "evaluated" but the detail record has no Amount field, so the evaluated dollars are dropped.')
  }
  if (layout.amountMode !== 'none' && !detailTokens.has('amount')) {
    problems.push('The detail record has no Amount field, so flat-dollar premiums (lead, hazard, jury, bereavement) cannot be paid.')
  }
  if (layout.departmentSource === 'cost_center' && !detailTokens.has('department')) {
    problems.push('Department source is the cost center, but the detail record has no department field.')
  }

  const hasHeaderCo = (layout.headerFields ?? []).some((f) => f.token === 'coCode')
  if (!hasHeaderCo) problems.push('The header record has no company code, so ADP cannot route the batch.')

  if (!(layout.fileNameTemplate ?? '').trim()) problems.push('The filename template is blank.')
  if (!(layout.retroFileNameTemplate ?? '').trim()) problems.push('The retro filename template is blank.')
  if ((layout.retroFileNameTemplate ?? '').trim() && layout.retroFileNameTemplate === layout.fileNameTemplate) {
    problems.push('The retro filename template is the same as the regular one, so a retro file would overwrite the regular file.')
  }
  const hasBatchType = [...(layout.headerFields ?? []), ...(layout.trailerFields ?? [])].some((f) => f.token === 'batchType')
  if (!hasBatchType) {
    problems.push('Neither the header nor the trailer carries the batch type, so a retro file is distinguishable from a regular one only by its filename.')
  }

  return problems
}
